import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import SwitcherToggle from "./SwitcherToggle";
import useTenantBranding from "../hooks/useTenantBranding";

export default function TopBar({
  user,
  onMenuClick,
  switcherOpen,
  onToggleSwitcher,
}) {
  const navigate = useNavigate();
  const [avatar, setAvatar] = useState(() => user?.photoURL || null);
  const branding = useTenantBranding(user);

  useEffect(() => {
    setAvatar(user?.photoURL || null);
  }, [user?.photoURL]);

  useEffect(() => {
    const auth = getAuth();
    const unsub = onAuthStateChanged(auth, (u) => {
      if (u?.photoURL) setAvatar(u.photoURL);
    });
    return () => unsub();
  }, []);

  const email = (user && (user.username || user.email)) || "";
  const initial = email ? email[0].toUpperCase() : "U";

  const brandTitle = useMemo(() => {
    if (branding?.enabled) {
      return String(
        branding.publicBrandName ||
        branding.brandName ||
        "LastWish Box"
      ).trim() || "LastWish Box";
    }
    return "LastWish Box";
  }, [branding]);

  const brandLogo = useMemo(() => {
    if (branding?.enabled && branding?.logoUrl) {
      return branding.logoUrl;
    }
    return "/logo-placeholder.png";
  }, [branding]);

  return (
    <header className="tb-wrap" role="banner">
      <div className="tb-inner">
        <Link to="/" className="tb-brand" aria-label="Home">
          <img
            src={brandLogo}
            alt={brandTitle}
            className="tb-logo"
          />
          <span className="tb-title">{brandTitle}</span>
        </Link>

        <div className="tb-actions">
          <Link
            to="/manager-select?switch=1"
            className="tb-switcher"
            aria-label="Switch account"
            title="Switch account"
          >
            <SwitcherToggle isOpen={!!switcherOpen} onToggle={onToggleSwitcher} />
          </Link>

          <button
            aria-label="Profile"
            title={email}
            className={"tb-avatar tb-profile" + (avatar ? " tb-avatar--img" : "")}
            onClick={() => navigate("/profile")}
            type="button"
          >
            {avatar ? <img src={avatar} alt="" /> : <span>{initial}</span>}
          </button>

          <button
            aria-label="Open menu"
            className="tb-hamburger tb-burger"
            onClick={onMenuClick}
            type="button"
          >
            <span className="tb-bar" /><span className="tb-bar" /><span className="tb-bar" />
          </button>
        </div>
      </div>

      <style>{`
        .tb-wrap{
          position:sticky; top:0; z-index:12000;
          background:rgba(255,255,255,.75);
          backdrop-filter:saturate(1.1) blur(10px);
          -webkit-backdrop-filter:saturate(1.1) blur(10px);
          border-bottom:1px solid #eee;
        }
        .tb-inner{
          display:flex; align-items:center; justify-content:space-between; gap:12px;
          padding:10px 14px; max-width:1200px; margin:0 auto;
        }

        .tb-brand{ display:inline-flex; align-items:center; gap:10px; text-decoration:none }
        .tb-logo{
          width:36px; height:36px; border-radius:10px;
          box-shadow:0 2px 10px rgba(0,0,0,.06);
          display:block; object-fit:contain;
          background:#fff;
        }
        .tb-title{
          font-weight:800; letter-spacing:.2px; color:#111827;
        }

        .tb-actions{ display:flex; align-items:center; gap:10px }
        .tb-switcher{ display:flex; align-items:center; text-decoration:none }
        .tb-avatar{
          width:40px; height:40px; border-radius:50%;
          border:1px solid #e5e7eb;
          background:linear-gradient(180deg,#fafafa,#f0f0f0);
          box-shadow:0 2px 10px rgba(0,0,0,.06);
          font-weight:800; color:#111827;
          display:flex; align-items:center; justify-content:center;
          cursor:pointer; overflow:hidden;
        }
        .tb-avatar img{ width:100%; height:100%; object-fit:cover; display:block }

        .tb-hamburger{
          display:none;
          flex-direction:column; gap:4px; width:40px; height:40px;
          align-items:center; justify-content:center;
          border:1px solid #e5e7eb; border-radius:12px;
          background:linear-gradient(180deg,#ffffffaa,#ffffff40);
          box-shadow:0 2px 10px rgba(0,0,0,.05); cursor:pointer;
        }
        .tb-bar{ display:block; width:18px; height:2px; background:#111827; border-radius:1px; }

        @media (max-width: 900px){
          .tb-inner{ padding:8px 10px; }
          .tb-hamburger{ display:inline-flex; }
          .tb-switcher{ order: 1; }
          .tb-profile { order: 2; }
          .tb-burger  { order: 3; }
        }
      `}</style>
    </header>
  );
}
